import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { FormControlLabel } from "@mui/material";
import { useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { ShowMenuIcon } from "@/Components/Icons/ShowMenuIcon";
import { Box } from "@/Components/v2/atoms/Box";
import { Button } from "@/Components/v2/atoms/Button";
import { Checkbox } from "@/Components/v2/atoms/Checkbox";
import { Divider } from "@/Components/v2/atoms/Divider";
import { InputAdornment } from "@/Components/v2/atoms/InputAdornment";
import { Menu } from "@/Components/v2/atoms/Menu";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { useDebouncedCallback } from "@/hooks/useDebounce";
import { ScenarioFilterTypeEnum, StatusEnum } from "@/pages/scenario-builder/types";

import { ColumnField, getAllColumnFields, getColumnHeaderName } from "./useScenariosPageColumns";

interface ScenarioToolBarProps {
  onStatusChange: (value: StatusEnum, checked: boolean) => void;
  onTypeChange: (value: ScenarioFilterTypeEnum, checked: boolean) => void;
  onSearchChange: (term: string) => void;
  onColumnVisibilityChange: (column: ColumnField, checked: boolean) => void;
  filters: ScenarioState["filters"];
  selectedColumns: ColumnField[];
  isGeoEnabled: boolean;
}

interface ScenarioState {
  selectedColumns: ColumnField[];
  filters: {
    status: StatusEnum[];
    type: ScenarioFilterTypeEnum[];
    searchTerm: string;
  };
}

// V2 only supports these filter types
const V2_FILTER_TYPES = [ScenarioFilterTypeEnum.Forecast, ScenarioFilterTypeEnum.Optimizer];

export const ORDERED_STATUS = [
  StatusEnum.Draft,
  StatusEnum.Completed,
  StatusEnum.Approved,
  StatusEnum.Processing,
  StatusEnum.Archived,
  StatusEnum.Error,
] as const;

export const STATUS_LABELS: Record<StatusEnum, string> = {
  [StatusEnum.Draft]: "Draft",
  [StatusEnum.Completed]: "Ready for Review",
  [StatusEnum.Approved]: "Approved",
  [StatusEnum.Archived]: "Archived",
  [StatusEnum.Error]: "Error",
  [StatusEnum.Processing]: "Simulating",
  [StatusEnum.Unknown]: "Unknown",
};

export const TYPE_LABELS: Record<ScenarioFilterTypeEnum, string> = {
  [ScenarioFilterTypeEnum.Forecast]: "Forecast",
  [ScenarioFilterTypeEnum.Optimizer]: "Optimizer",
  [ScenarioFilterTypeEnum.SuperOptimizer]: "Super Optimizer",
};

interface FilterMenuV2Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  filters: ScenarioState["filters"];
  onStatusChange: (value: StatusEnum, checked: boolean) => void;
  onTypeChange: (value: ScenarioFilterTypeEnum, checked: boolean) => void;
}

export const FilterMenu = ({
  anchorEl,
  open,
  onClose,
  filters,
  onStatusChange,
  onTypeChange,
}: FilterMenuV2Props) => {
  const handleStatusChange =
    (value: StatusEnum) => (event: React.ChangeEvent<HTMLInputElement>) => {
      onStatusChange(value, event.target.checked);
    };

  const handleTypeChange =
    (value: ScenarioFilterTypeEnum) => (event: React.ChangeEvent<HTMLInputElement>) => {
      onTypeChange(value, event.target.checked);
    };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Stack spacing={2} sx={{ p: 2, minWidth: 200 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} mb={1}>
            Filter by:
          </Typography>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Status:
          </Typography>
          <Stack>
            {ORDERED_STATUS.map((status) => {
              const label = STATUS_LABELS[status];
              return (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={filters.status.includes(status)}
                      onChange={handleStatusChange(status)}
                    />
                  }
                  label={label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontWeight: 600,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
        <Divider />
        <Box>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Type:
          </Typography>
          <Stack>
            {V2_FILTER_TYPES.map((filterType) => {
              const label = TYPE_LABELS[filterType];
              return (
                <FormControlLabel
                  key={filterType}
                  control={
                    <Checkbox
                      checked={filters.type.includes(filterType)}
                      onChange={handleTypeChange(filterType)}
                    />
                  }
                  label={label}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontWeight: 600,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </Menu>
  );
};

interface ViewMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedColumns: ColumnField[];
  onColumnVisibilityChange: (column: ColumnField, checked: boolean) => void;
  isGeoEnabled: boolean;
}

const ALL_COLUMNS = getAllColumnFields();
export const ViewMenu = ({
  anchorEl,
  open,
  onClose,
  selectedColumns,
  onColumnVisibilityChange,
  isGeoEnabled,
}: ViewMenuProps) => {
  const visibleColumns = useMemo(() => {
    return ALL_COLUMNS.filter((column) => {
      if (column === "geography" && !isGeoEnabled) return false;
      return true;
    });
  }, [isGeoEnabled]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Stack spacing={2} sx={{ p: 2, minWidth: 200 }}>
        <Typography variant="h5" fontWeight={600}>
          Show:
        </Typography>
        <Stack>
          {visibleColumns.map((column) => {
            const displayName = getColumnHeaderName(column);
            return (
              <FormControlLabel
                key={column}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(column)}
                    onChange={(e) => onColumnVisibilityChange(column, e.target.checked)}
                  />
                }
                label={displayName}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontWeight: 600,
                  },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Menu>
  );
};

export const ScenarioToolBar = ({
  onStatusChange,
  onTypeChange,
  onSearchChange,
  onColumnVisibilityChange,
  filters,
  selectedColumns,
  isGeoEnabled,
}: ScenarioToolBarProps) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [viewAnchorEl, setViewAnchorEl] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState(filters.searchTerm);

  const debouncedSearchChange = useDebouncedCallback(onSearchChange);

  useEffect(() => {
    setSearchValue(filters.searchTerm);
  }, [filters.searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearchChange(value);
  };

  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      <TextField
        placeholder="Search for a scenario"
        size="small"
        sx={{ maxWidth: "600px" }}
        fullWidth={true}
        value={searchValue}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          startIcon={<FilterListIcon />}
          variant="text"
          size="medium"
          onClick={(e) => setFilterAnchorEl(filterAnchorEl ? null : e.currentTarget)}
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: theme.palette.brandedTypography.textColor,
          }}
        >
          Filter
        </Button>

        <Button
          startIcon={<ShowMenuIcon />}
          variant="text"
          size="medium"
          onClick={(e) => setViewAnchorEl(viewAnchorEl ? null : e.currentTarget)}
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: theme.palette.brandedTypography.textColor,
          }}
        >
          Show/Hide
        </Button>
      </Stack>

      <FilterMenu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        filters={filters}
        onStatusChange={onStatusChange}
        onTypeChange={onTypeChange}
      />

      <ViewMenu
        anchorEl={viewAnchorEl}
        open={Boolean(viewAnchorEl)}
        onClose={() => setViewAnchorEl(null)}
        selectedColumns={selectedColumns}
        onColumnVisibilityChange={onColumnVisibilityChange}
        isGeoEnabled={isGeoEnabled}
      />
    </Stack>
  );
};
